import type { Moment } from 'moment';
import moment from 'moment';
import { _get } from './generic';
import type { TimelineGroupBase, TimelineItemBase } from '../types';
import type {
  ReactCalendarTimelineProps,
  ReactCalendarTimelineState,
  TimeSteps,
  Unit,
} from '../Timeline';
import {
  defaultItemHeightRatio,
  defaultLineHeight,
  defaultStackItems,
} from '../Timeline';
import { defaultKeys } from '../default-config';

/**
 * Calculate the ms / pixel ratio of the timeline state
 * @param {number} canvasTimeStart
 * @param {number} canvasTimeEnd
 * @param {number} canvasWidth
 * @returns {number}
 */
export function coordinateToTimeRatio(
  canvasTimeStart: number,
  canvasTimeEnd: number,
  canvasWidth: number,
): number {
  return (canvasTimeEnd - canvasTimeStart) / canvasWidth;
}

/**
 * Calculate the pixels / ms ratio of the timeline state
 * @param {number} canvasTimeStart
 * @param {number} canvasTimeEnd
 * @param {number} canvasWidth
 * @returns {number}
 */
export function timeToCoordinateRatio(
  canvasTimeStart: number,
  canvasTimeEnd: number,
  canvasWidth: number,
): number {
  return canvasWidth / (canvasTimeEnd - canvasTimeStart);
}

/**
 * For a given time, calculate the pixel position given timeline state
 * (timeline width in px, canvas time range)
 * @param {number} canvasTimeStart
 * @param {number} pixelsPerMs
 * @param {number} time
 * @returns {number}
 */
export function calculateXPositionForTime(
  canvasTimeStart: number,
  pixelsPerMs: number,
  time: number,
): number {
  const timeOffset = time - canvasTimeStart;
  return timeOffset * pixelsPerMs;
}

/**
 * For a given x position (leftOffset) in pixels, calculate time based on
 * timeline state (timeline width in px, canvas time range)
 * @param {number} canvasTimeStart
 * @param {number} timeRatio
 * @param {number} leftOffset
 * @returns {number}
 */
export function calculateTimeForXPosition(
  canvasTimeStart: number,
  timeRatio: number,
  leftOffset: number,
): number {
  const timeFromCanvasTimeStart = timeRatio * leftOffset;
  return timeFromCanvasTimeStart + canvasTimeStart;
}

export function iterateTimes(
  start: number,
  end: number,
  unit: Unit,
  timeSteps: TimeSteps,
  callback: (time: Moment, nextTime: Moment) => void,
) {
  let time = moment(start).startOf(unit);

  if (timeSteps[unit] && timeSteps[unit] > 1) {
    const value = time.get(unit);
    time.set(unit, value - (value % timeSteps[unit]));
  }

  while (time.valueOf() < end) {
    const nextTime = moment(time).add(
      timeSteps[unit] || 1,
      unit === 'isoWeek' ? 'week' : unit,
    );
    callback(time, nextTime);
    time = nextTime;
  }
}

// this function is VERY HOT as its used in Timeline.js render function
// TODO: check if there are performance implications here
// when "weeks" feature is implemented, this function will be modified heavily

/** determine the current rendered time unit based on timeline time span
 *
 * zoom: (in milliseconds) difference between time start and time end of timeline canvas
 * width: (in pixels) pixel width of timeline canvas
 * timeSteps: map of timeDividers with number to indicate step of each divider
 */

// the smallest cell we want to render is 17px
// this can be manipulated to make the breakpoints change more/less
// i.e. on zoom how often do we switch to the next unit of time
// I think this is the distance between cell lines
export const minCellWidth = 17;

// for supporting weeks, it's important to remember that each of these
// units has a natural progression to the other. i.e. a year is 12 months
// a month is 30 days, a day is 24 hours.
// with weeks this isn't the case so weeks needs to be handled specially
const timeDividers: Record<DateHeaderUnit, number> = {
  second: 1000,
  minute: 60,
  hour: 60,
  day: 24,
  week: 1,
  isoWeek: 1,
  month: 30,
  year: 12,
};

export function getMinUnit(
  zoom: number,
  width: number,
  timeSteps: Record<keyof typeof timeDividers, number>,
): DateHeaderUnit {
  let minUnit: DateHeaderUnit = 'year';

  // this timespan is in ms initially
  let nextTimeSpanInUnitContext = zoom;

  Object.keys(timeDividers).some((unit: DateHeaderUnit) => {
    // converts previous time span to current unit
    // (e.g. milliseconds to seconds, seconds to minutes, etc.)
    nextTimeSpanInUnitContext = nextTimeSpanInUnitContext / timeDividers[unit];

    // timeSteps is "
    // With what step to display different units. E.g. 15 for minute means only minutes 0, 15, 30 and 45 will be shown."
    // how many cells would be rendered given this time span, for this unit?
    // e.g. for time span of 60 minutes, and time step of 1, we would render 60 cells
    const cellsToBeRenderedForCurrentUnit =
      nextTimeSpanInUnitContext / timeSteps[unit];

    // what is happening here? why 3 if time steps are greater than 1??
    const cellWidthToUse =
      timeSteps[unit] && timeSteps[unit] > 1 ? 3 * minCellWidth : minCellWidth;

    // for the minWidth of a cell, how many cells would be rendered given
    // the current pixel width
    // i.e. f
    const minimumCellsToRenderUnit = width / cellWidthToUse;

    if (cellsToBeRenderedForCurrentUnit < minimumCellsToRenderUnit) {
      // for the current zoom, the number of cells we'd need to render all parts of this unit
      // is less than the minimum number of cells needed at minimum cell width
      minUnit = unit;
      return true;
    }
  });

  return minUnit;
}

export type DateHeaderUnit =
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'isoWeek'
  | 'month'
  | 'year';

const nextUnits: Record<DateHeaderUnit, DateHeaderUnit> = {
  second: 'minute',
  minute: 'hour',
  hour: 'day',
  day: 'month',
  week: 'month',
  isoWeek: 'month',
  month: 'year',
  year: 'year',
};

export function getNextUnit(unit: keyof typeof nextUnits): DateHeaderUnit {
  const next = nextUnits[unit];
  if (!next) {
    throw new Error(`unit ${unit} in not acceptable`);
  }
  return next;
}

/**
 * get the new start and new end time of item that is being
 * dragged or resized
 * @param {*} itemTimeStart original item time in milliseconds
 * @param {*} itemTimeEnd original item time in milliseconds
 * @param {*} dragTime new start time if item is dragged in milliseconds
 * @param {*} isDragging is item being dragged
 * @param {*} isResizing is item being resized
 * @param {`right` or `left`} resizingEdge resize edge
 * @param {*} resizeTime new resize time in milliseconds
 */
export function calculateInteractionNewTimes({
  itemTimeStart,
  itemTimeEnd,
  dragTime,
  isDragging,
  isResizing,
  resizingEdge,
  resizeTime,
}: {
  itemTimeStart: number;
  itemTimeEnd: number;
  dragTime: number | null;
  isDragging: boolean;
  isResizing: boolean;
  resizingEdge: null | 'left' | 'right';
  resizeTime: null | number;
}) {
  const originalItemRange = itemTimeEnd - itemTimeStart;
  const itemStart =
    isResizing && resizingEdge === 'left' ? resizeTime : itemTimeStart;
  const itemEnd =
    isResizing && resizingEdge === 'right' ? resizeTime : itemTimeEnd;
  const drag = isDragging && dragTime;
  return [
    drag ? dragTime : itemStart,
    drag ? dragTime + originalItemRange : itemEnd,
  ];
}

/**
 * Get the order of groups based on their keys
 * @param {*} groups array of groups
 * @param {*} keys the keys object
 * @returns Ordered hash of objects with their array index and group
 */
export function getGroupOrders<CustomGroup extends TimelineGroupBase>(
  groups: CustomGroup[],
  keys: {
    groupIdKey: keyof CustomGroup;
  },
) {
  const { groupIdKey } = keys;

  const groupOrders: Record<
    string,
    {
      index: number;
      group: CustomGroup;
    }
  > = {};

  for (let i = 0; i < groups.length; i++) {
    groupOrders[_get(groups[i], groupIdKey) as string] = {
      index: i,
      group: groups[i],
    };
  }

  return groupOrders;
}

/**
 * Adds items relevant to each group to the result of getGroupOrders
 * @param {*} items list of all items
 * @param {*} groupOrders the result of getGroupOrders
 */
export function getGroupedItems<CustomGroup extends TimelineGroupBase>(
  items: ItemDimensions[],
  groupOrders: ReturnType<typeof getGroupOrders<CustomGroup>>,
) {
  const groupedItems: Record<
    string,
    {
      index: number;
      group: CustomGroup;
      items: ItemDimensions[];
    }
  > = {};
  const keys = Object.keys(groupOrders);
  // Initialize with result object for each group
  for (let i = 0; i < keys.length; i++) {
    const groupOrder = groupOrders[keys[i]];
    groupedItems[i] = {
      index: groupOrder.index,
      group: groupOrder.group,
      items: [],
    };
  }

  // Populate groups
  for (let i = 0; i < items.length; i++) {
    const order = items[i].dimensions.order;
    if (order !== undefined) {
      const groupItem = groupedItems[order.index];
      if (groupItem) {
        groupItem.items.push(items[i]);
      }
    }
  }

  return groupedItems;
}

export function getVisibleItems<CustomItem extends TimelineItemBase>(
  items: CustomItem[],
  canvasTimeStart: number,
  canvasTimeEnd: number,
  keys: {
    itemTimeStartKey: keyof CustomItem;
    itemTimeEndKey: keyof CustomItem;
  },
) {
  const { itemTimeStartKey, itemTimeEndKey } = keys;

  return items.filter((item) => {
    return (
      (_get(item, itemTimeStartKey) as number) <= canvasTimeEnd &&
      (_get(item, itemTimeEndKey) as number) >= canvasTimeStart
    );
  });
}

const EPSILON = 0.001;

export type Collision = {
  collisionLeft: number;
  collisionWidth: number;
  top: number | null;
  height: number;
};

export function collision(
  a: Collision,
  b: Collision,
  collisionPadding = EPSILON,
) {
  // 2d collisions detection - https://developer.mozilla.org/en-US/docs/Games/Techniques/2D_collision_detection
  const verticalMargin = 0;

  const aTop = a.top ?? 0;
  const bTop = b.top ?? 0;
  const aHeight = b.height;
  const bHeight = a.height;
  const aCollisionLeft = a.collisionLeft;
  const bCollisionLeft = b.collisionLeft;
  const aCollisionWidth = a.collisionWidth;
  const bCollisionWidth = b.collisionWidth;

  return (
    aCollisionLeft + collisionPadding < bCollisionLeft + bCollisionWidth &&
    aCollisionLeft + aCollisionWidth - collisionPadding > bCollisionLeft &&
    aTop - verticalMargin + collisionPadding < bTop + aHeight &&
    aTop + bHeight + verticalMargin - collisionPadding > bTop
  );
}

/**
 * Calculate the position of a given item for a group that
 * is being stacked
 */
export function groupStack(
  lineHeight: number,
  item: ItemDimensions,
  group: ItemDimensions[],
  groupHeight: number,
  groupTop: number,
  itemIndex: number,
): number {
  // calculate non-overlapping positions
  let curHeight = groupHeight;
  const dimensions = item.dimensions;
  const verticalMargin = (lineHeight - dimensions.height) / 2;
  if (dimensions.stack && dimensions.top === null) {
    dimensions.top = groupTop + verticalMargin;
    curHeight = Math.max(curHeight, lineHeight);
    let collidingItem;
    do {
      collidingItem = null;
      // Items are placed from i=0 onwards, only check items with index < i
      for (let j = itemIndex - 1; j >= 0; j--) {
        const other = group[j];
        const otherDimensions = other.dimensions;
        if (
          otherDimensions.top !== null &&
          otherDimensions.stack &&
          collision(dimensions, otherDimensions)
        ) {
          collidingItem = other;
          // There is a collision. Reposition the items above the colliding element
          dimensions.top = otherDimensions.top + lineHeight;
          curHeight = Math.max(
            curHeight,
            dimensions.top + dimensions.height + verticalMargin - groupTop,
          );
          break;
        }
      }
    } while (collidingItem);
  }
  return curHeight;
}

// Calculate the position of this item for a group that is not being stacked
export function groupNoStack(
  lineHeight: number,
  item: ItemDimensions,
  groupHeight: number,
  groupTop: number,
) {
  if (item.dimensions.top === null) {
    const verticalMargin = (lineHeight - item.dimensions.height) / 2;
    item.dimensions.top = groupTop + verticalMargin;
    groupHeight = Math.max(groupHeight, lineHeight);
  }
  return groupHeight;
}

/**
 * Stack all groups
 * @param {*} itemsDimensions items to be stacked
 * @param {*} groupOrders the groupOrders object
 * @param {*} lineHeight
 * @param {*} stackItems should items be stacked?
 */
export function stackAll(
  itemsDimensions: ItemDimensions[],
  groupOrders: ReturnType<typeof getGroupOrders>,
  lineHeight: number,
  stackItems: boolean,
) {
  const groupHeights: number[] = [];
  const groupTops: number[] = [];

  const groupedItems = getGroupedItems(itemsDimensions, groupOrders);

  let groupTop = 0;
  for (const index in groupedItems) {
    const groupItems = groupedItems[index];
    const { items, group } = groupItems;

    // Is group being stacked?
    const isGroupStacked = group.stackItems ?? stackItems;
    const groupHeight = stackGroup(items, isGroupStacked, lineHeight, groupTop);
    // If group height is overridden, push new height
    // Do this late as item position still needs to be calculated
    groupTops.push(groupTop);
    const height = group.height ?? Math.max(groupHeight, lineHeight);
    groupHeights.push(height);
    groupTop += height;
  }

  return {
    height: groupTop,
    groupHeights,
    groupTops,
  };
}

/**
 *
 * @param {*} itemsDimensions
 * @param {*} isGroupStacked
 * @param {*} lineHeight
 * @param {*} groupTop
 */
export function stackGroup(
  itemsDimensions: ItemDimensions[],
  isGroupStacked: boolean,
  lineHeight: number,
  groupTop: number,
) {
  let groupHeight = 0;
  // Find positions for each item in group
  for (let itemIndex = 0; itemIndex < itemsDimensions.length; itemIndex++) {
    groupHeight = isGroupStacked
      ? groupStack(
          lineHeight,
          itemsDimensions[itemIndex],
          itemsDimensions,
          groupHeight,
          groupTop,
          itemIndex,
        )
      : groupNoStack(
          lineHeight,
          itemsDimensions[itemIndex],
          groupHeight,
          groupTop,
        );
  }
  return groupHeight;
}

/**
 * Stack the items that will be visible
 * within the canvas area
 * @param {item[]} items
 * @param {group[]} groups
 * @param {number} canvasTimeStart
 * @param {number} canvasTimeEnd
 * @param {number} pixelsPerMs
 * @param {*} keys
 * @param {number} lineHeight
 * @param {number} itemHeightRatio
 * @param {boolean} stackItems
 * @param {*} draggingItem
 * @param {*} resizingItem
 * @param {number} dragTime
 * @param {left or right} resizingEdge
 * @param {number} resizeTime
 * @param {number} newGroupOrder
 */
export function stackTimelineItems<
  CustomItem extends TimelineItemBase,
  CustomGroup extends TimelineGroupBase,
>(
  items: CustomItem[],
  groups: CustomGroup[],
  canvasTimeStart: number,
  canvasTimeEnd: number,
  pixelsPerMs: number,
  keys: {
    itemIdKey: keyof CustomItem;
    itemTimeStartKey: keyof CustomItem;
    itemTimeEndKey: keyof CustomItem;
    itemGroupKey: keyof CustomItem;
    groupIdKey: keyof CustomGroup;
  },
  lineHeight: number,
  itemHeightRatio: number,
  stackItems: boolean,
  draggingItem: boolean,
  resizingItem: boolean,
  dragTime: number | null,
  resizingEdge: null | 'left' | 'right',
  resizeTime: null | number,
  newGroupOrder: null | number,
) {
  const visibleItems = getVisibleItems<CustomItem>(
    items,
    canvasTimeStart,
    canvasTimeEnd,
    keys,
  );
  const visibleItemsWithInteraction = visibleItems.map((item) =>
    getItemWithInteractions({
      item,
      keys,
      draggingItem,
      resizingItem,
      dragTime,
      resizingEdge,
      resizeTime,
      groups,
      newGroupOrder,
    }),
  );

  // if there are no groups return an empty array of dimensions
  if (groups.length === 0) {
    return {
      dimensionItems: [],
      height: 0,
      groupHeights: [],
      groupTops: [],
    };
  }

  // Get the order of groups based on their id key
  const groupOrders = getGroupOrders(groups, keys);
  const dimensionItems = visibleItemsWithInteraction.map((item) =>
    getItemDimensions({
      item,
      keys,
      canvasTimeStart,
      pixelsPerMs,
      groupOrders,
      lineHeight,
      itemHeightRatio,
    }),
  );
  // Get a new array of groupOrders holding the stacked items
  const { height, groupHeights, groupTops } = stackAll(
    dimensionItems,
    groupOrders,
    lineHeight,
    stackItems,
  );
  return { dimensionItems, height, groupHeights, groupTops };
}

/**
 * get canvas width from visible width
 * @param {number} width
 * @param {number} buffer
 */
export function getCanvasWidth(width: number, buffer: number = 3) {
  return width * buffer;
}

export type ItemDimensions = {
  id: string | number;
  dimensions: {
    stack: boolean;
    top: null | number;
    left: number;
    collisionLeft: number;
    width: number;
    collisionWidth: number;
    order?: {
      index: number;
    };
    height: number;
  };
};

/**
 * get item's position, dimensions and collisions
 * @param {*} item
 * @param {*} keys
 * @param {*} canvasTimeStart
 * @param {*} pixelsPerMs
 * @param {*} groupOrders
 * @param {*} lineHeight
 * @param {*} itemHeightRatio
 */
export function getItemDimensions<
  CustomItem extends TimelineItemBase,
  CustomGroup extends TimelineGroupBase,
>({
  item,
  keys,
  canvasTimeStart,
  pixelsPerMs,
  groupOrders,
  lineHeight,
  itemHeightRatio,
}: {
  item: CustomItem;
  keys: {
    itemIdKey: keyof CustomItem;
    itemTimeStartKey: keyof CustomItem;
    itemTimeEndKey: keyof CustomItem;
    itemGroupKey: keyof CustomItem;
  };
  canvasTimeStart: number;
  pixelsPerMs: number;
  groupOrders: ReturnType<typeof getGroupOrders<CustomGroup>>;
  lineHeight: number;
  itemHeightRatio: number;
}): ItemDimensions {
  const id = _get(item, keys.itemIdKey) as string | number;
  const itemTimeStart = _get(item, keys.itemTimeStartKey) as number;
  const itemTimeEnd = _get(item, keys.itemTimeEndKey) as number;

  const itemTimeRange = itemTimeEnd - itemTimeStart;

  // restrict startTime and endTime to be bounded by canvasTimeStart and canvasTimeEnd
  // const effectiveStartTime = Math.max(itemTimeStart, canvasTimeStart)
  // const effectiveEndTime = Math.min(itemTimeEnd, canvasTimeEnd)
  // NOTE: These are overridden in this fork to allow items to start/end outside of timeline bounds
  const effectiveStartTime = itemTimeStart;
  const effectiveEndTime = itemTimeEnd;

  const left = calculateXPositionForTime(
    canvasTimeStart,
    pixelsPerMs,
    effectiveStartTime,
  );
  const right = calculateXPositionForTime(
    canvasTimeStart,
    pixelsPerMs,
    effectiveEndTime,
  );
  const itemWidth = right - left;

  const dimensions = {
    left: left,
    width: Math.max(itemWidth, 3),
    collisionLeft: itemTimeStart,
    collisionWidth: itemTimeRange,
    top: null,
    order: groupOrders[_get(item, keys.itemGroupKey) as string],
    stack: !item.isOverlay,
    height: lineHeight * itemHeightRatio,
  };

  return {
    id,
    dimensions,
  };
}

/**
 * get new item with changed  `itemTimeStart` , `itemTimeEnd` and `itemGroupKey` according to
 * user interaction is dragging an item and resize left and right
 * @param {*} item
 * @param {*} keys
 * @param {*} draggingItem
 * @param {*} resizingItem
 * @param {*} dragTime
 * @param {*} resizingEdge
 * @param {*} resizeTime
 * @param {*} groups
 * @param {*} newGroupOrder
 */
export function getItemWithInteractions<
  CustomItem extends TimelineItemBase,
  CustomGroup extends TimelineGroupBase,
>({
  item,
  keys,
  draggingItem,
  resizingItem,
  dragTime,
  resizingEdge,
  resizeTime,
  groups,
  newGroupOrder,
}: {
  item: CustomItem;
  keys: {
    itemIdKey: keyof CustomItem;
    itemTimeStartKey: keyof CustomItem;
    itemTimeEndKey: keyof CustomItem;
    itemGroupKey: keyof CustomItem;
    groupIdKey: keyof CustomGroup;
  };
  draggingItem: boolean;
  resizingItem: boolean;
  dragTime: number | null;
  resizingEdge: null | 'left' | 'right';
  resizeTime: null | number;
  groups: CustomGroup[];
  newGroupOrder: null | number;
}) {
  if (!resizingItem && !draggingItem) return item;
  const itemId = _get(item, keys.itemIdKey);
  const isDragging = itemId === draggingItem;
  const isResizing = itemId === resizingItem;
  const [itemTimeStart, itemTimeEnd] = calculateInteractionNewTimes({
    itemTimeStart: _get(item, keys.itemTimeStartKey),
    itemTimeEnd: _get(item, keys.itemTimeEndKey),
    isDragging,
    isResizing,
    dragTime,
    resizingEdge,
    resizeTime,
  });
  return {
    ...item,
    [keys.itemTimeStartKey]: itemTimeStart,
    [keys.itemTimeEndKey]: itemTimeEnd,
    [keys.itemGroupKey]:
      isDragging && newGroupOrder !== null
        ? _get(groups[newGroupOrder], keys.groupIdKey)
        : _get(item, keys.itemGroupKey),
  };
}

/**
 * get canvas start and end time from visible start and end time
 * @param {number} visibleTimeStart
 * @param {number} visibleTimeEnd
 */
export function getCanvasBoundariesFromVisibleTime(
  visibleTimeStart: number,
  visibleTimeEnd: number,
) {
  const zoom = visibleTimeEnd - visibleTimeStart;
  const canvasTimeStart = visibleTimeStart - zoom;
  const canvasTimeEnd = visibleTimeEnd + zoom;
  return [canvasTimeStart, canvasTimeEnd];
}

/**
 * Get the canvas area for a given visible time
 * Will shift the start/end of the canvas if the visible time
 * does not fit within the existing
 * @param {number} visibleTimeStart
 * @param {number} visibleTimeEnd
 * @param {boolean} forceUpdateDimensions
 * @param {*} items
 * @param {*} groups
 * @param {*} props
 * @param {*} state
 */
export function calculateScrollCanvas<
  CustomItem extends TimelineItemBase = TimelineItemBase,
  CustomGroup extends TimelineGroupBase = TimelineGroupBase,
  Props extends ReactCalendarTimelineProps<
    CustomItem,
    CustomGroup
  > = ReactCalendarTimelineProps<CustomItem, CustomGroup>,
>(
  visibleTimeStart: number,
  visibleTimeEnd: number,
  forceUpdateDimensions: boolean,
  items: Props['items'],
  groups: Props['groups'],
  props: Props,
  state: ReactCalendarTimelineState,
):
  | {
      canvasTimeEnd: number;
      visibleTimeEnd: number;
      visibleTimeStart: number;
      canvasTimeStart: number;
      dimensionItems: ItemDimensions[];
      groupTops: number[];
      groupHeights: number[];
      height: number;
    }
  | Pick<ReactCalendarTimelineState, 'visibleTimeStart' | 'visibleTimeEnd'> {
  const oldCanvasTimeStart = state.canvasTimeStart;
  const oldZoom = state.visibleTimeEnd - state.visibleTimeStart;
  const newZoom = visibleTimeEnd - visibleTimeStart;

  // Check if the current canvas covers the new times
  const canKeepCanvas =
    newZoom === oldZoom &&
    visibleTimeStart >= oldCanvasTimeStart + oldZoom * 0.5 &&
    visibleTimeStart <= oldCanvasTimeStart + oldZoom * 1.5 &&
    visibleTimeEnd >= oldCanvasTimeStart + oldZoom * 1.5 &&
    visibleTimeEnd <= oldCanvasTimeStart + oldZoom * 2.5;

  if (!canKeepCanvas || forceUpdateDimensions) {
    const [canvasTimeStart, canvasTimeEnd] = getCanvasBoundariesFromVisibleTime(
      visibleTimeStart,
      visibleTimeEnd,
    );

    const canvasWidth = getCanvasWidth(state.width);
    const timeRatio = coordinateToTimeRatio(
      canvasTimeStart,
      canvasTimeEnd,
      canvasWidth,
    );
    const pixelsPerMs = timeToCoordinateRatio(
      canvasTimeStart,
      canvasTimeEnd,
      canvasWidth,
    );

    const keys =
      props.keys ?? (defaultKeys as NonNullable<(typeof props)['keys']>);

    // The canvas cannot be kept, so calculate the new items position
    return Object.assign(
      {
        visibleTimeStart,
        visibleTimeEnd,
        canvasTimeStart,
        canvasTimeEnd,
        timeRatio,
        pixelsPerMs,
      },
      stackTimelineItems(
        items,
        groups,
        canvasTimeStart,
        canvasTimeEnd,
        pixelsPerMs,
        keys,
        props.lineHeight ?? defaultLineHeight,
        props.itemHeightRatio ?? defaultItemHeightRatio,
        props.stackItems ?? defaultStackItems,
        Boolean(state.draggingItem),
        Boolean(state.resizingItem),
        state.dragTime,
        state.resizingEdge,
        state.resizeTime,
        state.newGroupOrder,
      ),
    );
  }
  return { visibleTimeStart, visibleTimeEnd };
}
